/* eslint-disable react/display-name, no-param-reassign */

import React from 'react';

import { InfoCircleOutlined, PlusSquareOutlined, SwapOutlined } from '@ant-design/icons';
import { Tooltip, Tag, Row, Col, message, Button, Statistic } from 'antd';
import {
  createMenuMappingV2AxiosInstance,
  createMenuManagementAxiosInstance,
} from 'createAxiosInstance';
import dayjs from 'dayjs';
import i18n from 'i18n';

import {
  MENU_ITEMS,
  MENU_MODIFIERS,
  MENU_MODIFIERS_LIMIT,
  POS_ITEMS,
  POS_MODIFIERS,
  IS_ADDITION,
  IS_MAIN,
  MODIFIER,
  UUID_EXTERNAL_ID,
  IGNORE,
  AS_INSTRUCTION,
  MAPPING_STATUS,
  MAPPING_STATUS_COLUMN,
  AS_ADDITION,
  REPLACES_MAIN,
} from 'constants/mapping';

import { isSubstring } from 'helpers/validation';

import Fader from 'components/Cuboh/Fader';

import { CAMEL_TYPES } from 'constants/menuManagement';

const NS = 'helpers';

// Module-level helpers can't useTranslation; defaultValue covers the async namespace load
i18n.loadNamespaces(NS);

export const modifierType = {
  modifier: MODIFIER,
  isAddition: IS_ADDITION,
  isMain: IS_MAIN,
};

export const publishMapping = async ({ companyID }) => {
  const axiosInstance = await createMenuMappingV2AxiosInstance();
  await axiosInstance.post(`/V2/company/${companyID}/publish`);
};

// NOTE: for now modifiers all go via V1
export const setMappingV2 = async ({ companyID, posID, priceLevel, ids, mappingType }) => {
  const axiosInstance = await createMenuMappingV2AxiosInstance();
  const {
    data: { data: items },
  } = await axiosInstance.patch(
    `/V2/company/${companyID}/menu/${mappingType === MENU_ITEMS ? 'items' : 'modifiers'}`,
    {
      posID,
      priceLevel,
      ids,
    },
  );

  return items.reduce((previous, current) => {
    previous[current.id] = { ...current, key: current.id };
    return previous;
  }, {});
};

export const changeMenuModifier = async ({ companyID, ids, payload }) => {
  const axiosInstance = await createMenuMappingV2AxiosInstance();
  const {
    data: { data: items },
  } = await axiosInstance.patch(`/V2/company/${companyID}/menu/modifiers`, {
    ...payload,
    ids,
  });

  return items.reduce((previous, current) => {
    previous[current.id] = { ...current, key: current.id };
    return previous;
  }, {});
};

export const changeMenuItemV2 = async ({ companyID, ids, payload }) => {
  const axiosInstance = await createMenuMappingV2AxiosInstance();
  const {
    data: { data: items },
  } = await axiosInstance.patch(`/V2/company/${companyID}/menu/items`, {
    ...payload,
    ids,
  });

  return items.reduce((previous, current) => {
    previous[current.id] = { ...current, key: current.id };
    return previous;
  }, {});
};

/**
 * Render price helper method
 * @param {integer} price: A numeric amount
 * @returns Formatted price to US locale OR `Unknown`
 */
/* eslint-disable-next-line no-confusing-arrow */
export const renderPrice = price =>
  price !== null && price !== undefined
    ? (price / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    : i18n.t('Mapping.unknownPrice', { ns: NS, defaultValue: 'Unknown' });

/**
 * Transform a tree value into a rendered cell
 *
 * @param {*} nameOnPos: The name of the item on the POS
 * @param {*} price: The price of the item
 * @param {*} priceOption: The option number associated with the price
 * @param {*} posID: The ID of the price-priceOption paire on the POS
 * @returns a formatted row cell for the PosTreeSelect
 */
export const renderTreeNode = (nameOnPos, price, priceOption, posID) => (
  <Row>
    <Col flex="auto">{`${nameOnPos}`}</Col>
    <Col flex="100px">{`${renderPrice(price)}`}</Col>
    <Col flex="100px">{priceOption}</Col>
    <Col flex="100px">{posID}</Col>
  </Row>
);

export const mapPosTreeNode = (priceOption, isLeaf, pId = 0) => ({
  id: pId === 0 ? priceOption.id : `${priceOption.id}-${priceOption.level}`,
  pId,
  key: JSON.stringify(priceOption),
  value: JSON.stringify(priceOption),
  title: renderTreeNode(priceOption.name, priceOption.price, priceOption.level, priceOption.id),
  isLeaf,
  rawValue: priceOption,
});

// Helper for basic price cells
export const renderPriceCell = (value, row) => (
  <Fader value={`${value}-${row.id}`}>{renderPrice(value)}</Fader>
);

// Helper for basic text cells
export const renderTextCell = (value, row) => <Fader value={`${value}-${row.id}`}>{value}</Fader>;

// Helper for matched cell
export const renderMatchedCell = (value, id) => {
  const tag = value ? <Tag color="success">Live</Tag> : <Tag color="warning">New</Tag>;
  return <Fader value={`${value}-${id}`}>{tag}</Fader>;
};

export const getKeyTitle = key => {
  switch (key) {
    case MENU_ITEMS:
      return 'Menu Items';
    case POS_ITEMS:
      return 'POS Items';
    case MENU_MODIFIERS:
      return 'Menu Modifiers';
    case POS_MODIFIERS:
      return 'POS Modifiers';
    default:
      throw new Error(`Unknown key: ${key}`);
  }
};

/**
 * Render the tag for the modifier type
 * @param {'is-addition' | 'is-addition' | 'modifier'} type
 * @returns an Icon for the modfifier by its type
 */
export const renderModifierType = type => {
  switch (type) {
    case modifierType.isAddition:
      return <Tag icon={<PlusSquareOutlined />}>Additional Item</Tag>;
    case modifierType.isMain:
      return <Tag icon={<SwapOutlined />}>Replaces Main</Tag>;
    default:
      return null;
  }
};

// in this case we want to keep going until the last page
// even if there is no data as some pages might be empty but we're not done processing
/**
 * Pull data from each page
 *
 * @param {*} request: (limit, offset) => axios request
 * @param {*} limit: number of items to request per page
 * @returns concatenated array of all data
 */
const getAllPagesV2 = async (request, setLoadingProgress, limit = 10000) => {
  let data = [];
  let offset = 0;
  let finished = false;

  while (!finished) {
    /* eslint-disable-next-line no-await-in-loop */
    const response = await request(limit, offset);
    const {
      data: {
        data: items,
        pagination: { page, totalPageCount },
      },
    } = response;
    setLoadingProgress((page / totalPageCount) * 100);

    if (page >= totalPageCount) {
      finished = true;
    } else {
      offset += limit;
    }

    data = [...data, ...items];
  }
  return data;
};

/**
 * Apply custom filter state to the datasource and return valid rows
 * @param {object} dataSource: The menu item table datasource
 * @param {object} customFilters: The custom filter state for the table
 * @returns The datasource with custom filters applied
 */
export const filterDataSource = (dataSource, customFilters) => {
  const { approved, searchFilters } = customFilters;

  return [
    ...dataSource.filter(row => {
      if (approved !== '' && row.matched !== approved) {
        return false;
      }

      // Loop through filter fields object, and check substring match
      return Object.keys(searchFilters).reduce((accumulator, filterField) => {
        // if the row does have the filter field as a key, don't filter
        if (!(filterField in row)) {
          return true;
        }

        // if value has the key and is null
        if (!row[filterField]) {
          return false;
        }

        if (!isSubstring(row[filterField], searchFilters[filterField])) {
          return false;
        }

        // Pass current value
        return accumulator;
      }, true);
    }),
  ];
};

// eslint-disable-next-line react/prop-types
export const renderTooltip = ({ title, tooltip }) => (
  <Tooltip title={tooltip}>
    {title} <InfoCircleOutlined />
  </Tooltip>
);

export const getMenuMainItemsV2 = async ({ companyID, setLoadingProgress = () => {} }) => {
  const axiosInstance = await createMenuMappingV2AxiosInstance();
  const data = await getAllPagesV2(
    (limit, offset) =>
      axiosInstance.get(`/api/mapping/main-items/${companyID}?offset=${offset}&limit=${limit}`),
    setLoadingProgress,
  );

  /** Normalize the menu items by ID */
  return data.reduce((previous, current) => {
    previous[current.id] = { ...current, key: current.id };
    return previous;
  }, {});
};

export const getPosMainItemsV2 = async ({ companyID, setLoadingProgress = () => {} }) => {
  const axiosInstance = await createMenuMappingV2AxiosInstance();
  const data = await getAllPagesV2(
    (limit, offset) =>
      axiosInstance.get(`/api/mapping/pos-main-items/${companyID}?offset=${offset}&limit=${limit}`),
    setLoadingProgress,
  );

  /** Normalize the menu items by ID */
  return data
    .sort((a, b) => a.nameOnPos?.localeCompare(b.nameOnPos))
    .reduce((previous, current) => {
      previous[current.id] = {
        ...current,
        key: current.id,
        priceOptions: current.priceOptions.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.level || '']: {
              ...curr,
            },
          }),
          {},
        ),
      };
      return previous;
    }, {});
};

export const getMenuModifiersV2 = async ({ companyID, setLoadingProgress = () => {} }) => {
  const axiosInstance = await createMenuMappingV2AxiosInstance();
  const data = await getAllPagesV2(
    (limit, offset) =>
      axiosInstance.get(`/api/mapping/modifier-list/${companyID}?offset=${offset}&limit=${limit}`),
    setLoadingProgress,
    MENU_MODIFIERS_LIMIT,
  );

  /** Normalize the menu items by ID */
  return data.reduce((previous, current) => {
    previous[current.id] = { ...current, key: current.id };
    return previous;
  }, {});
};

export const getPosModifiersV2 = async ({
  companyID,
  posMainItemIDs = [],
  setLoadingProgress = () => {},
}) => {
  const axiosInstance = await createMenuMappingV2AxiosInstance();
  // remove duplicate and empty items and then join into a string
  const posMainItems = [...new Set(posMainItemIDs)]
    .filter(item => !!item)
    .map(item => encodeURIComponent(item))
    .join(',');
  const data = await getAllPagesV2(
    (limit, offset) =>
      axiosInstance.get(
        `/api/mapping/pos-modifier-list/${companyID}?posMainItems=${posMainItems}&offset=${offset}&limit=${limit}`,
      ),
    setLoadingProgress,
  );

  /** Normalize the menu items by ID */
  return data
    .sort((a, b) => a.nameOnPos?.localeCompare(b.nameOnPos))
    .reduce((previous, current) => {
      previous[current.id] = {
        ...current,
        key: current.id,
        priceOptions: current.priceOptions.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.level || '']: {
              ...curr,
            },
          }),
          {},
        ),
      };
      return previous;
    }, {});
};

export const aggregateMenuModifiers = menuModifiers =>
  Object.keys(menuModifiers).reduce((accumulator, id) => {
    const modifier = menuModifiers[id];
    const partitionKey = {
      appPrice: modifier.appPrice,
      nameOnApp: modifier.nameOnApp,
      posID: modifier.posID,
      priceLevel: modifier.priceLevel,
      type: modifier.modifierType,
    };

    const key = JSON.stringify(partitionKey);
    accumulator[key] = accumulator[key]
      ? {
          ...partitionKey,
          key,
          confidence: accumulator[key].confidence || modifier.confidence,
          subTableData: [...accumulator[key].subTableData, modifier],
          matched: accumulator[key].matched ? modifier.matched : accumulator[key].matched,
        }
      : {
          ...partitionKey,
          key,
          confidence: modifier.confidence,
          subTableData: [modifier],
          matched: modifier.matched,
        };

    return accumulator;
  }, {});

export const transformPOSDataToTree = data =>
  Object.values(data)
    .sort((a, b) => a.nameOnPos?.localeCompare(b.nameOnPos))
    .reduce((previous, { priceOptions }) => {
      const [firstOption, ...otherOptions] = Object.values(priceOptions);
      return [
        ...previous,
        mapPosTreeNode(firstOption, otherOptions.length === 0),
        ...otherOptions.map(option => mapPosTreeNode(option, true, firstOption.id)),
      ];
    }, []);

export const cancellableNotification = async ({
  promise,
  loadingMessage = 'Loading...',
  successMessage = 'Success!',
  errorMessage = 'Error',
  cancelDuration = 6, // 6 Seconds to cancel
}) => {
  let requestCancelled = false;
  const messageKey = `MAPPING-${dayjs().unix()}`;
  const deadline = dayjs().add(cancelDuration, 'seconds');

  const hide = message.info({
    key: messageKey,
    duration: cancelDuration,
    content: (
      <>
        <span>{loadingMessage}</span>
        <Statistic.Countdown title="Cancel Deadline" value={deadline} format="ss" />
        <Button
          danger
          size="small"
          onClick={() => {
            requestCancelled = true;
            hide();
          }}
        >
          Cancel
        </Button>
      </>
    ),
  });

  const requestPromise = new Promise((resolve, reject) => {
    setTimeout(async () => {
      if (requestCancelled) {
        return;
      }

      message.loading({
        key: messageKey,
        duration: 0,
        content: loadingMessage,
      });

      try {
        const result = await promise();
        message.success({
          key: messageKey,
          duration: 2,
          content: successMessage,
        });
        resolve(result);
      } catch (e) {
        message.error({
          key: messageKey,
          duration: 2,
          content: errorMessage,
        });
        reject();
      }
    }, cancelDuration * 1000);
  });

  return requestPromise;
};

/** *************************** MENU MANAGEMENT MAPPING *********************************** */

const normalizeData = (data, normalizeKey) => {
  if (!data) return {};
  const normalizedData = data.reduce((accumulator, item) => {
    accumulator[item[normalizeKey]] = item;
    return accumulator;
  }, {});
  return normalizedData;
};

export const fetchMappingFormattedMenu = async ({ companyID, merchantID, menuID, itemType }) => {
  const axios = await createMenuManagementAxiosInstance();

  const response = await axios.get(
    `company/${companyID}/merchant/${merchantID}/map/menu/${menuID}/app/${itemType}`,
  );

  return normalizeData(response?.data, 'uuidUuid');
};

export const fetchMappingFormattedPosMenu = async ({ companyID, itemType, merchantID = 0 }) => {
  const axios = await createMenuManagementAxiosInstance();
  const fetchLimit = 1000;

  const allData = [];
  let totalItemCount = 1;
  let totalPages = 1;
  let currentPage = 0;
  for (
      let offset = 0;
      allData.length < totalItemCount && currentPage < totalPages;
      offset += fetchLimit
    ) {
    // eslint-disable-next-line no-await-in-loop
    const response = await axios.get(
      `company/${companyID}/merchant/${merchantID}/map/pos/${itemType}`,
      {
        params: {
          offset,
          limit: fetchLimit,
        },
      },
    );
    if (!response?.data?.data?.length) break;
    currentPage = response?.data?.pagination?.page ?? currentPage + 1;
    // first loop. get total item count
    if (offset === 0) {
      totalItemCount = response?.data?.pagination?.totalItemCount ?? 0;
      totalPages = response?.data?.pagination?.totalPageCount ?? 0;
    }

    allData.push(...(response?.data?.data ?? []));
  }

  return normalizeData(allData, 'uuidExternalId');
};

export const applyMappingData = async ({ companyID, merchantID, menuID, mappingData }) => {
  const axios = await createMenuManagementAxiosInstance();

  const response = await axios.patch(
    `company/${companyID}/merchant/${merchantID}/map/menu/${menuID}/apply`,
    mappingData,
  );

  return response?.data;
};

export const handleAutoMapRequest = async ({
  companyID,
  merchantID,
  menuUuid,
  itemType,
  unmapAll,
  forceMap,
}) => {
  const axios = await createMenuManagementAxiosInstance();

  const response = await axios.patch(`company/${companyID}/merchant/${merchantID}/map/auto`, {
    menuUuid,
    itemType,
    unmapAll,
    forceMap,
  });

  return response?.data;
};

export const findMappingStatus = ({ item, itemType, posMenu }) => {
  // return the value that was set if it is there
  if (item[MAPPING_STATUS_COLUMN]) return item.mappingStatus;
  if (item[AS_INSTRUCTION]) return MAPPING_STATUS.MAPPED;
  if (item[IGNORE]) return MAPPING_STATUS.MAPPED;
  if (!item[UUID_EXTERNAL_ID]) return MAPPING_STATUS.UNMAPPED;
  if (item[AS_ADDITION] || item[REPLACES_MAIN]) {
    if (!posMenu[CAMEL_TYPES.items][item[UUID_EXTERNAL_ID]]) return MAPPING_STATUS.POS_MISSING;
  } else if (!posMenu[itemType]?.[item[UUID_EXTERNAL_ID]]) return MAPPING_STATUS.POS_MISSING;

  return MAPPING_STATUS.MAPPED;
};

export const readableName = (type, count) => {
  const pluralNames = {
    [CAMEL_TYPES.items]: 'Items',
    [CAMEL_TYPES.modifiers]: 'Modifiers',
  };
  const singularNames = {
    [CAMEL_TYPES.items]: 'Item',
    [CAMEL_TYPES.modifiers]: 'Modifier',
  };
  if (!count || count > 1) return pluralNames[type];
  return singularNames[type];
};

export const buildMappingMessageText = ({ appMenu, itemType, posMenu }) => {
  const count = num => `${num} ${readableName(itemType, num)}`;

  if (!appMenu[itemType]) {
    return { baseMessage: '', badPosItemsMessage: '', saveRequired: '', alertType: '' };
  }

  const appMenuOfType = Object.values(appMenu[itemType]) ?? [];

  const mappedItems = appMenuOfType.filter(
    item =>
      findMappingStatus({ item, posMenu, itemType }) === MAPPING_STATUS.MAPPED ||
      findMappingStatus({ item, posMenu, itemType }) === MAPPING_STATUS.STAGED_MAPPED,
  ).length;

  const unmappedItems = appMenuOfType.filter(
    item =>
      findMappingStatus({ item, posMenu, itemType }) === MAPPING_STATUS.UNMAPPED ||
      (!item[UUID_EXTERNAL_ID] && !item[AS_INSTRUCTION] && !item[IGNORE]),
  ).length;

  const newMapped = appMenuOfType.filter(item =>
    findMappingStatus({ item, posMenu, itemType }).includes(MAPPING_STATUS.STAGED),
  ).length;

  let baseMessage = `${count(mappedItems)} mapped`.toLowerCase();
  baseMessage += `, ${count(unmappedItems)} unmapped`.toLowerCase();

  const saveRequired = newMapped ? ' (edited)' : '';
  const successMessage =
    `mapped ${count(mappedItems)}, and unmapped ${count(unmappedItems)}.`.toLowerCase();

  return { baseMessage, saveRequired, successMessage };
};

export const formatConfidence = item => {
  if (!item.confidence) return 'N/A';
  if (item.confidence > 100) return '100%';
  if (item.confidence < 0) return '0%';
  return `${item.confidence}%`;
};

export const formatPrice = price => {
  if (price == null) return i18n.t('Mapping.unknownPosPrice', { ns: NS, defaultValue: 'Unknown POS price' });
  return `$${parseFloat(price / 100).toFixed(2)}`;
};
